/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { Box, IconButton, MobileStepper } from '@mui/material';
import { StyledEngineProvider } from '@mui/material/styles';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { ThemeProvider } from 'styled-components';
import { BEAM_TYPE } from '../../../../../utils/BeamUtils';
import { dateToUTCDate, dateToUTCTime } from '../../../../../utils/DateTimeUtils';
import { EMPTY_FIELD_STRING_PLACEHOLDER } from '../../../../../utils/StringUtils';
import appTheme from '../../../../common/theme/appTheme.json';
import StyledProps from '../../../../common/theme/AppThemeInterface';
import ChartsSeriesColorWrapper from '../ChartsSeriesColorWrapper';
import { AnnotationPoint } from '../ChartWrappers/TimelineChartHelperFunctioners';
import ChartCustomTooltipHeader from './ChartCustomTooltipHeader';

const DISABLED_OPACITY = 0.6;
const DEFAULT_OPACITY = 1;

export interface ChartCustomTooltipProps {
  point?: Partial<AnnotationPoint>;
  tooltipStep?: number;
  date?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  container: HTMLElement;
}

const ChartCustomTimelineTooltip = (props: ChartCustomTooltipProps) => {
  const { point, date, tooltipStep, container, onNext, onPrevious } = props;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [tooltipContainerStyle, setTooltipContainerStyle] = useState({
    position: 'absolute',
    left: `${container.clientWidth / 2 - 100}px`,
    minWidth: '200px',
    top: `-135px`,
    pointerEvents: 'all',
  });

  useEffect(() => {
    setTooltipContainerStyle(prevStyle => {
      if (containerRef && containerRef.current) {
        return {
          ...prevStyle,
          top: `${container.clientHeight / 2 - containerRef.current.clientHeight}px`,
          left: `${container.clientWidth / 2 - containerRef.current.clientWidth / 2}px`,
        };
      }
      return prevStyle;
    });
  }, [container, containerRef, point, tooltipStep]);

  // determine if a handover icon
  if (point !== undefined) {
    let currentBeamType = get(point, 'custom.currentBeamType', EMPTY_FIELD_STRING_PLACEHOLDER);
    let nextBeamType = get(point, 'custom.nextBeamType', EMPTY_FIELD_STRING_PLACEHOLDER);
    let currentBeamValue = get(point, 'custom.currentBeamValue', EMPTY_FIELD_STRING_PLACEHOLDER);
    let currentViasatBeamValue = get(point, 'custom.currentViasatBeamValue');
    let nextBeamValue = get(point, 'custom.nextBeamValue', EMPTY_FIELD_STRING_PLACEHOLDER);
    let nextViasatBeamValue = get(point, 'custom.nextViasatBeamValue');
    let pointDate = date;
    // @ts-ignore
    const isCluster = point.isCluster;
    // @ts-ignore
    const clusterAmount = point.clusterPointsAmount;

    if (isCluster) {
      const pointShown = tooltipStep ? tooltipStep : 0;
      currentBeamType = get(point, `clusteredData[${pointShown}].options.custom.currentBeamType`, EMPTY_FIELD_STRING_PLACEHOLDER);
      nextBeamType = get(point, `clusteredData[${pointShown}].options.custom.nextBeamType`, EMPTY_FIELD_STRING_PLACEHOLDER);
      currentBeamValue = get(point, `clusteredData[${pointShown}].options.custom.currentBeamValue`, EMPTY_FIELD_STRING_PLACEHOLDER);
      currentViasatBeamValue = get(point, `clusteredData[${pointShown}].options.custom.currentViasatBeamValue`);
      nextBeamValue = get(point, `clusteredData[${pointShown}].options.custom.nextBeamValue`, EMPTY_FIELD_STRING_PLACEHOLDER);
      nextViasatBeamValue = get(point, `clusteredData[${pointShown}].options.custom.nextViasatBeamValue`);
      pointDate = get(point, `clusteredData[${pointShown}].x`, pointDate);
    }
    let currentBeamTypeName = currentBeamType;
    let nextBeamTypeName = nextBeamType;

    let tooltipHeader = `${currentBeamTypeName}-to-${nextBeamTypeName} Handoff`;
    let showTo = true;
    let showFrom = true;

    const isSpotBeamHandover =
      currentBeamTypeName === BEAM_TYPE.KA &&
      nextBeamTypeName === BEAM_TYPE.KA &&
      !isNil(currentViasatBeamValue) &&
      !isNil(nextViasatBeamValue) &&
      currentViasatBeamValue === nextViasatBeamValue;

    if (isSpotBeamHandover) {
      tooltipHeader = 'Ka Spot-Beam Handoff';
    } else if (currentBeamType === BEAM_TYPE.OFFLINE) {
      tooltipHeader = 'Connection Established';
      showFrom = false;
    } else if (nextBeamType === BEAM_TYPE.OFFLINE) {
      tooltipHeader = 'Connection Lost';
      showTo = false;
    } else if (currentBeamType !== nextBeamType) {
      tooltipHeader = `${currentBeamTypeName}-to-${nextBeamTypeName} Switch`;
    }

    return createPortal(
      <ChartsSeriesColorWrapper style={tooltipContainerStyle} ref={containerRef}>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={appTheme}>
            <StyledWrapper className="chart__custom__tooltip" data-test="chart__custom__tooltip">
              <ChartCustomTooltipHeader>
                <Box>{pointDate ? dateToUTCDate(new Date(pointDate)) : EMPTY_FIELD_STRING_PLACEHOLDER}</Box>
                <Box sx={{ color: '#ffffff', fontSize: '20px', lineHeight: '20px', fontWeight: 700 }}>
                  {pointDate ? dateToUTCTime(new Date(pointDate)) : EMPTY_FIELD_STRING_PLACEHOLDER}
                </Box>
              </ChartCustomTooltipHeader>

              <StyledTooltipBody>
                <StyledNetworkEventLabel
                  sx={{ color: appTheme.primaryTextColor, fontWeight: 600, fontSize: '16px', lineHeight: '20px' }}
                >
                  <div data-test="chart__custom__tooltip--header">{tooltipHeader}</div>
                </StyledNetworkEventLabel>
                {showFrom && (
                  <StyledLabel>
                    <div data-test="chart__custom__tooltip--from">{`From: ${
                      !isNil(currentViasatBeamValue) && currentBeamTypeName === BEAM_TYPE.KA && !isSpotBeamHandover
                        ? `${currentViasatBeamValue} (${currentBeamValue})`
                        : currentBeamValue
                    }`}</div>
                  </StyledLabel>
                )}
                {showTo && (
                  <StyledLabel>
                    <div data-test="chart__custom__tooltip--to">{`To: ${
                      !isNil(nextViasatBeamValue) && nextBeamTypeName === BEAM_TYPE.KA && !isSpotBeamHandover
                        ? `${nextViasatBeamValue} (${nextBeamValue})`
                        : nextBeamValue
                    }`}</div>
                  </StyledLabel>
                )}
              </StyledTooltipBody>
              {isCluster && (
                <MobileStepper
                  variant="dots"
                  steps={clusterAmount}
                  position="static"
                  activeStep={tooltipStep}
                  className="ftv__timeline__tooltip--stepper"
                  data-test="chart__custom__tooltip--stepper"
                  nextButton={
                    <IconButton
                      size="small"
                      disableFocusRipple
                      disableRipple
                      style={{
                        pointerEvents: tooltipStep === clusterAmount - 1 ? 'none' : 'all',
                        backgroundColor: '#61DED8',
                        width: '22px',
                        height: '22px',
                        opacity: tooltipStep === clusterAmount - 1 ? DISABLED_OPACITY : DEFAULT_OPACITY,
                      }}
                      className="tooltip-next"
                      data-test="chart__custom__tooltip--next"
                      disabled={tooltipStep === clusterAmount - 1}
                      onClick={onNext}
                    >
                      <ArrowForward
                        style={{
                          fill: '#141D24',
                          stroke: 'none',
                          width: '18px',
                          height: '18px',
                        }}
                      />
                    </IconButton>
                  }
                  backButton={
                    <IconButton
                      size="small"
                      disableFocusRipple
                      disableRipple
                      style={{
                        pointerEvents: tooltipStep === 0 ? 'none' : 'all',
                        backgroundColor: '#61DED8',
                        color: '#141D24',
                        width: '22px',
                        height: '22px',
                        opacity: tooltipStep === 0 ? DISABLED_OPACITY : DEFAULT_OPACITY,
                      }}
                      className="tooltip-previous"
                      data-test="chart__custom__tooltip--previous"
                      disabled={tooltipStep === 0}
                      onClick={onPrevious}
                    >
                      <ArrowBack
                        style={{
                          fill: '#141D24',
                          stroke: 'none',
                          width: '18px',
                          height: '18px',
                        }}
                      />
                    </IconButton>
                  }
                />
              )}
              <StyledTooltipPointerContainer>
                <StyledTooltipPointer>
                  <svg height="9" width="9" style={{ transform: 'rotate(45deg' }}>
                    <rect height={9} width={9} style={{ fill: '#141D24', strokeWidth: 0 }} />
                  </svg>
                </StyledTooltipPointer>
              </StyledTooltipPointerContainer>
            </StyledWrapper>
          </ThemeProvider>
        </StyledEngineProvider>
      </ChartsSeriesColorWrapper>,
      container,
    );
  }

  // do not return a tooltip when on the timeline segments
  return null;
};

export default ChartCustomTimelineTooltip;

const StyledWrapper = styled.div`
  overflow: visible;
  z-index: 5;
  position: relative;
  border-radius: 8px;
  background-color: #000000;

  .ftv__timeline__tooltip--stepper {
    background-color: #000000;
    // body content padding bottom is 17px - 8px gives 9px of padding between stepper and content
    margin-top: -8px;
    padding: 0px 15px 13px;

    .MuiMobileStepper-dot {
      background-color: #465967;
      width: 6px;
      height: 6px;
    }

    .MuiMobileStepper-dotActive {
      background-color: #61ded8;
    }
  }
`;
const StyledNetworkEventLabel = styled.div`
  color: ${({ theme }: StyledProps) => theme.primaryTextColor};
  fontsize: 12px;
  margin-bottom: 8px;
  font-weight: 600;
`;

const StyledTooltipBody = styled.div`
  position: relative;
  display: inline-block;
  padding-bottom: 16px;
  padding-right: 16px;
  padding-left: 16px;
`;

const StyledLabel = styled.span`
  position: relative;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }: StyledProps) => theme.primaryTextColor};
  font-family: ${({ theme }: StyledProps) => theme.appFontFamily};
`;

const StyledTooltipPointerContainer = styled.div`
  background-color: rgba(0, 0, 0, 0);
  width: 100%;
  height: 9px;
  position: absolute;
  overflow: visible;
  bottom: 0px;
`;
const StyledTooltipPointer = styled.div`
  margin-top: -1.5px;
  background-color: #5a8fff;
  position: relative;
  width: 0;
  height: 0;
  margin-left: auto;
  margin-right: auto;
  right: 4px;
`;
